import type { ComponentProps, ReactNode } from 'react';
import { Button, Heading, Image, InlineStack, Stack } from '@customerio/pluma-components/react';
import type { PopularIntegrationListItem } from '../../catalog/images';
import { BlankSlate } from '../../directory/components/blank-slate';
import { createJourneysHref } from '../view-model/journeys-href';
import * as styles from './connections-blank-slate.css';

type PopularIntegrationButtonProps = {
  integration: PopularIntegrationListItem;
  workspaceId: string;
};

/**
 * A single popular-integration shortcut. `createJourneysHref` resolves both
 * kinds of target: a Journeys route name (forms) becomes an embedded-app URL,
 * while an already-absolute Pipelines URL is used verbatim.
 */
function PopularIntegrationButton({ integration, workspaceId }: PopularIntegrationButtonProps) {
  if (integration.path == null || integration.path === '') {
    return null;
  }
  const href = createJourneysHref({ path: integration.path, dynamicSegment: integration.dynamicSegment }, workspaceId);
  return (
    <Button
      data-test-integrations-blank-slate-popular-integrations-button={integration.name}
      variant="tertiary"
      href={href}
      isExternal={false}
    >
      <InlineStack gap="125" alignBlock="center">
        <Image src={integration.imageSrc ?? ''} width="18" height="18" data-test-img alt="" />
        {integration.name}
      </InlineStack>
    </Button>
  );
}

type ConnectionsBlankSlateProps = {
  icon: ComponentProps<typeof BlankSlate>['icon'];
  title: string;
  description: string;
  popularIntegrationsText: string;
  popularIntegrations: PopularIntegrationListItem[];
  workspaceId: string;
  children: ReactNode;
};

/**
 * The Connections tab empty state: the shared directory `BlankSlate` (icon /
 * title / description / CTA) plus a "popular connections" shortcut row — the
 * part the Ember `integrations/blank-slate` adds over the directory's variant.
 */
export function ConnectionsBlankSlate({
  icon,
  title,
  description,
  popularIntegrationsText,
  popularIntegrations,
  workspaceId,
  children,
}: ConnectionsBlankSlateProps) {
  return (
    <Stack alignInline="center" gap="300" className={styles.blankSlate}>
      <BlankSlate icon={icon} title={title} description={description}>
        {children}
      </BlankSlate>
      <Heading
        data-test-integrations-blank-slate-popular-integrations-text
        level="4"
        color="placeholder"
        textAlign="center"
      >
        {popularIntegrationsText}
      </Heading>
      <InlineStack data-test-integrations-blank-slate-popular-integrations alignInline="center" gap="200" wrap>
        {popularIntegrations.map((integration) => (
          <PopularIntegrationButton key={integration.name} integration={integration} workspaceId={workspaceId} />
        ))}
      </InlineStack>
    </Stack>
  );
}
